import { useEffect } from 'react';
import { useParams } from 'react-router';
import makeStyles from '@mui/styles/makeStyles';
import { Breadcrumbs, Card, CardContent, CardHeader, Grid, Link, Theme, Typography } from '@mui/material';
import { useAccountsByCustomerStore, useApplicationsByCustomerStore, useCustomerStore } from '@lib/store';
import { usePrevious, useRouteHook } from '@shared/hooks';
import { PermissionsUtils, RoutePaths } from '@shared/utils';
import { PermissionLevel, PermissionType } from '@shared/types';
import { AccountsList } from './widgets';
import { ApplicationsTable } from '../Applications/widgets/ApplicationsTable';
import { DelayedLinearProgress } from '@shared/widgets';
import { Button } from '@shared/widgets';

const useStyles = makeStyles<Theme>((theme) => ({
  postTransactionContainer: {
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: theme.palette.primary.main,
  },
  card: {
    marginBottom: theme.spacing(2),
  },
  applicationsHeader: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
  },
}));

const LOADING_TITLE = 'Loading customer details';

export const CustomerDetailsPage = () => {
  const { selectedCustomerId } = useParams<{ selectedCustomerId: string }>();
  const { navigate } = useRouteHook();

  const { loading: loadingCustomers, data: customer, getCustomer, clear: clearCustomer } = useCustomerStore();
  const { loading: loadingApplications, data: applications, getApplicationsByCustomer } = useApplicationsByCustomerStore();
  const { loading: loadingAccounts, data: accounts, getAccountsByCustomer } = useAccountsByCustomerStore();

  const classes = useStyles();

  useEffect(() => {
    if (!selectedCustomerId) return;
    getCustomer(selectedCustomerId);
    getApplicationsByCustomer(selectedCustomerId);

    return () => clearCustomer();
  }, []);

  const prevCustomer = usePrevious(customer);
  useEffect(() => {
    if (!customer) return;
    if (prevCustomer === customer) return;
    getAccountsByCustomer({ customerId: customer.id });
  }, [customer, prevCustomer]);

  const canCreateApplication = PermissionsUtils.hasPermission(PermissionType.APPLICATIONS, PermissionLevel.WRITE);
  return (
    <>
      <Breadcrumbs aria-label='breadcrumb' sx={{ marginBottom: '12px' }}>
        <Link sx={{ textTransform: 'uppercase', fontSize: '12px' }} underline='hover' color='inherit' href={RoutePaths.customers.onNavigate()}>
          Customers
        </Link>
        {customer && (
          <Link sx={{ textTransform: 'uppercase', fontSize: '12px' }} underline='none'>
            {customer.fullname}
          </Link>
        )}
      </Breadcrumbs>
      <Grid container alignContent='center'>
        <Grid item xs={12} md={4}>
          <DelayedLinearProgress loading={loadingCustomers} />
          <Card>
            <CardHeader title='Customer Details' />
            <CardContent>
              <Typography component='span' variant='body1'>
                <Grid container>
                  <Grid item xs={4}>
                    Title
                  </Grid>
                  <Grid item xs={8}>
                    {!customer ? LOADING_TITLE : customer.title || '-'}
                  </Grid>
                  <Grid item xs={4}>
                    Full Name
                  </Grid>
                  <Grid item xs={8}>
                    {!customer ? LOADING_TITLE : customer.fullname || '-'}
                  </Grid>
                  <Grid item xs={4}>
                    Passport/ID
                  </Grid>
                  <Grid item xs={8}>
                    {!customer ? LOADING_TITLE : `${customer.passport || '-'}`}
                  </Grid>
                  <Grid item xs={4}>
                    Address
                  </Grid>
                  <Grid item xs={8}>
                    {!customer ? LOADING_TITLE : `${customer.address || '-'}`}
                  </Grid>
                  <Grid item xs={4}>
                    Mobile Number
                  </Grid>
                  <Grid item xs={8}>
                    {!customer ? LOADING_TITLE : `+${customer.mobileNumber || '-'}`}
                  </Grid>
                  <>
                    <Grid item xs={4}>
                      Alternate Mobile Number
                    </Grid>
                    <Grid item xs={8}>
                      {!customer ? LOADING_TITLE : `${customer.alternateMobile || '-'}`}
                    </Grid>
                  </>
                  <Grid item xs={4}>
                    Email Address
                  </Grid>
                  <Grid item xs={8}>
                    {!customer ? LOADING_TITLE : `${customer.email || '-'}`}
                  </Grid>
                </Grid>
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={8}>
          {PermissionsUtils.hasPermission(PermissionType.ACCOUNTS, PermissionLevel.READ) && (
            <>
              <DelayedLinearProgress loading={loadingCustomers || loadingAccounts} />
              <Card className={classes.card}>
                <CardHeader title='Accounts' />
                {!!accounts.length && <AccountsList data={accounts} selectedCustomerId={selectedCustomerId!} />}
                {!accounts.length && !loadingCustomers && !loadingAccounts && (
                  <CardContent>
                    <Typography align='center'>No data to display.</Typography>
                  </CardContent>
                )}
              </Card>
            </>
          )}
          <DelayedLinearProgress loading={loadingApplications} />
          <Card className={classes.card}>
            <CardHeader
              title={
                <div className={classes.applicationsHeader}>
                  <span>Applications</span>
                  {canCreateApplication && (
                    <Button
                      variant='contained'
                      color='primary'
                      onClick={() => navigate(RoutePaths.applicationsCustomerNew.onNavigate(selectedCustomerId!))}
                    >
                      Create New Application
                    </Button>
                  )}
                </div>
              }
            />
            {!!applications.length && <ApplicationsTable data={applications} loading={loadingApplications} />}
            {!applications.length && !loadingApplications && (
              <CardContent>
                <Typography align='center'>
                  No data to display.
                  <br />
                  Start by performing a search.
                </Typography>
              </CardContent>
            )}
          </Card>
        </Grid>
      </Grid>
    </>
  );
};
